import React, { useEffect, useState } from 'react';
import { Button, Header, Message, Table } from 'semantic-ui-react';
import { API, copy, showSuccess, showWarning } from '../helpers';
import {
  ImageOutputWarningsV912,
  RequestAuditTraceV97,
} from './requestAuditV97';
import { RetryAuditTimelineV1021 } from './retryAuditV1021';
import {
  customerMoneyFieldsV1014,
  customerMoneyV1014,
} from './customerMoneyV1014';
import {
  customerLogDetailV822,
  customerLogEndpointsV822,
  customerLogTextV822,
  customerLogValueV822,
} from './customerLogV822';

const JsonSection = ({ title, value }) => (
  <details className='customer-log-json-v822'>
    <summary>{title}</summary>
    <pre>{JSON.stringify(value, null, 2)}</pre>
  </details>
);

const CustomerLogRequestDetailV822 = ({
  log,
  customerScope,
  admin,
  root = false,
  onDisclosure,
  onRetryAudit,
  t,
}) => {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: false,
  });
  const [revision, setRevision] = useState(0);
  const requestID = log?.request_id;
  const showDiagnostics =
    (admin && root) || state.data?.log_debug_enabled === true;
  const endpoint =
    admin && !root
      ? null
      : customerLogEndpointsV822(customerScope, admin).detail(requestID);
  const text = (key) => customerLogTextV822(t, key);

  useEffect(() => {
    if (!requestID || !endpoint) return;
    let active = true;
    const controller = new AbortController();
    setState({ loading: true, data: null, error: false });
    API.get(endpoint, {
      params: admin ? { customer_scope: customerScope } : {},
      signal: controller.signal,
    })
      .then((response) => {
        if (!active) return;
        if (!response?.data?.success || !response.data.data?.request) {
          onDisclosure?.(false, null);
          setState({ loading: false, data: null, error: true });
          return;
        }
        setState({ loading: false, data: response.data.data, error: false });
        onRetryAudit?.(requestID, response.data.data.retry_audit_v1021);
        onDisclosure?.(
          response.data.data.log_debug_enabled === true,
          response.data.data.request
        );
      })
      .catch(() => {
        if (active) {
          onDisclosure?.(false, null);
          setState({ loading: false, data: null, error: true });
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    admin,
    customerScope,
    endpoint,
    requestID,
    revision,
    onDisclosure,
    onRetryAudit,
  ]);

  if (!requestID || !endpoint) return null;
  const detail = customerLogDetailV822(state.data, log, showDiagnostics);
  const money = customerMoneyV1014(detail.request);
  const meters = showDiagnostics
    ? (detail.attempts || []).flatMap((attempt) =>
        (attempt.meters || []).map((meter) => ({
          ...meter,
          attempt_no: attempt.attempt_no,
        }))
      )
    : detail.meters;

  return (
    <section
      className='customer-log-detail-v822'
      aria-label={text('structured')}
    >
      {!state.loading && !state.error && state.data?.client_ip_status ? (
        <div className='log-detail-meta-v7' data-testid='log-client-ip-v109'>
          <div>
            <span>{text('client_ip')}</span>
            <code>
              {state.data.client_ip ||
                text(
                  state.data.client_ip_status === 'unavailable'
                    ? 'ip_unavailable'
                    : 'ip_not_recorded'
                )}
            </code>
          </div>
        </div>
      ) : null}
      <Header as='h4'>{text('structured')}</Header>
      {state.loading ? <p role='status'>{text('loading')}</p> : null}
      {state.error ? (
        <Message warning>
          {text('detail_failed')}
          <Button
            size='mini'
            onClick={() => setRevision((current) => current + 1)}
          >
            {text('retry')}
          </Button>
        </Message>
      ) : null}
      {state.data ? (
        <>
          {money && (
            <dl
              className='log-detail-meta-v7'
              data-testid='customer-money-v1014'
            >
              {customerMoneyFieldsV1014.map(([field, zh, en]) => (
                <div key={field}>
                  <dt>
                    {t(`customer_money_v1014.${field}`, {
                      defaultValue: `${zh} / ${en}`,
                    })}
                  </dt>
                  <dd>
                    <code>{money[field] ?? '-'}</code>
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <JsonSection title={text('request')} value={detail.request} />
          <RetryAuditTimelineV1021
            value={detail.retry_audit_v1021}
            requestID={requestID}
          />
          {root && state.data.request_audit_v97 && (
            <RequestAuditTraceV97 data={state.data.request_audit_v97} />
          )}
          {root && state.data.request_audit_status_v97 && (
            <Message
              content={
                state.data.request_audit_status_v97 === 'not_recorded'
                  ? '此请求没有 V9.7 路由审计记录'
                  : '路由审计暂不可用'
              }
            />
          )}
          {showDiagnostics ? (
            <>
              {!(root && state.data.request_audit_v97) && (
                <ImageOutputWarningsV912
                  warnings={detail.image_output_diagnostics_v1022}
                />
              )}
              <JsonSection
                title={text('attempts')}
                value={detail.attempts || []}
              />
              {detail.google != null ? (
                <JsonSection title={text('google')} value={detail.google} />
              ) : null}
              {detail.billing != null ? (
                <JsonSection title={text('billing')} value={detail.billing} />
              ) : null}
            </>
          ) : null}
          <Header as='h5'>{text('meters')}</Header>
          {meters.length ? (
            <div className='customer-log-meter-scroll-v822'>
              <Table compact celled unstackable>
                <Table.Header>
                  <Table.Row>
                    {(showDiagnostics
                      ? [
                          'attempts',
                          'meter',
                          'quantity',
                          'unit',
                          'measurement_status',
                          'source',
                          'attributes',
                        ]
                      : [
                          'meter',
                          'quantity',
                          'unit',
                          'measurement_status',
                          'source',
                          'attributes',
                        ]
                    ).map((field) => (
                      <Table.HeaderCell key={field}>
                        {text(field)}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {meters.map((meter, index) => (
                    <Table.Row key={index}>
                      {showDiagnostics ? (
                        <Table.Cell>
                          {customerLogValueV822(meter.attempt_no)}
                        </Table.Cell>
                      ) : null}
                      <Table.Cell>
                        {customerLogValueV822(meter.meter)}
                      </Table.Cell>
                      <Table.Cell>
                        {['missing', 'unknown', 'unavailable'].includes(
                          meter.measurement_status
                        )
                          ? '-'
                          : customerLogValueV822(meter.quantity)}
                      </Table.Cell>
                      <Table.Cell>
                        {customerLogValueV822(meter.unit)}
                      </Table.Cell>
                      <Table.Cell>
                        {customerLogValueV822(meter.measurement_status)}
                      </Table.Cell>
                      <Table.Cell>
                        {customerLogValueV822(meter.source)}
                      </Table.Cell>
                      <Table.Cell>
                        {meter.attributes == null ? (
                          '-'
                        ) : (
                          <JsonSection
                            title={text('attributes')}
                            value={meter.attributes}
                          />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <p>{text('no_meters')}</p>
          )}
          <Button
            basic
            size='small'
            onClick={async () => {
              try {
                if (
                  await copy(
                    JSON.stringify(
                      root
                        ? {
                            ...detail,
                            request_audit_v97: state.data.request_audit_v97,
                          }
                        : detail,
                      null,
                      2
                    )
                  )
                )
                  showSuccess(t('log.messages.copy_success'));
                else showWarning(t('log.messages.copy_failed'));
              } catch {
                showWarning(t('log.messages.copy_failed'));
              }
            }}
          >
            {text('copy_json')}
          </Button>
        </>
      ) : null}
    </section>
  );
};

export default CustomerLogRequestDetailV822;
